import React from 'react';

import {
  Box,
  Button
} from '@mui/material';
import {
  InitiateReturnModal,
  ReceiveReturnAuthorizationModal,
  activityTemplates,
  routeDirectory,
  selectFields,
  useTabSearchParams
} from 'modules';

import { modalUtil } from 'iron-forge-react-modules';
import { useNavigate } from 'react-router-dom';
import * as TabBar from './TabBar/TabBar';
import * as shared from './shared';
import * as AwaitingApprovalTab from './AwaitingApprovalTab/AwaitingApprovalTab';
import * as AwaitingShippingLabelTab from './AwaitingShippingLabelTab/AwaitingShippingLabelTab';
import * as AwaitingItemReceiptTab from './AwaitingItemReceiptTab/AwaitingItemReceiptTab';
import * as AwaitingAcceptanceTab from './AwaitingAcceptanceTab/AwaitingAcceptanceTab';
import * as AwaitingRefundTab from './AwaitingRefundTab/AwaitingRefundTab';
import * as LookUpReturnAuthorizationModal from './LookUpReturnAuthorizationModal/LookUpReturnAuthorizationModal';



const { FC: StoreSelectField } = selectFields.StoreSelectField.genComponentAndControllerGenerator({
  salesChannel: 'ecommerce'
});



export const ReturnAuthorizationsActivity: React.FC = () => {
  const params = shared.useReturnAuthorizationActivityParams();
  const [tabId, setTabId] = useTabSearchParams({
    defaultTabId: '',
    paramName: 'storeId'
  });
  const {
    modalType2IsOpen,
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'lookUpReturnAuthorization',
    'receiveReturnAuthorization',
    'findOrderRma',
    'createReturnAuthorization'
  ]);
  const navigate = useNavigate();



  const handleClickReceiveItems = React.useCallback(
    () => {
      setModalIsOpen('lookUpReturnAuthorization', true);
    },
    [setModalIsOpen]
  );

  const handleSelectReturnAuthorization = React.useCallback(
    (returnAuthorizationId: string) => {
      setModalIsOpen('lookUpReturnAuthorization', false);
      setModalIsOpen('receiveReturnAuthorization', returnAuthorizationId);
    },
    [setModalIsOpen]
  );

  const handleClickViewAllRmas = React.useCallback(
    () => {
      navigate(routeDirectory.admin.returnAuthorizations_deprecated.path());
    },
    [navigate]
  );

  const handleClickFindOrderRma = React.useCallback(
    () => {
      setModalIsOpen('findOrderRma', true);
    },
    [setModalIsOpen]
  );

  const handleClickCreateReturn = React.useCallback(
    () => {
      setModalIsOpen('createReturnAuthorization', true);
    },
    [setModalIsOpen]
  );



  const storeId = tabId || undefined;

  return (
    <>
      <activityTemplates.AdminQueuingActivityTemplate.FC
        elButtons={(
          <>
            <Button
              variant="contained"
              onClick={handleClickFindOrderRma}>
              Find Order/RMA
            </Button>

            <Button
              variant="contained"
              onClick={handleClickCreateReturn}>
              Create Return
            </Button>

            <Button
              variant="contained"
              onClick={handleClickReceiveItems}>
              Receive Items
            </Button>
          </>
        )}
        elTabBar={<TabBar.FC storeId={storeId} />}
        elTabBarActions={(
          <>
            <Box sx={{ minWidth: 368 }}>
              <StoreSelectField
                disableDefaultMargins
                label="Store"
                selectProps={{
                  type: 'singleSelect',
                  value: storeId ? { id: storeId, displayName: '' } : null,
                  onChange: store => setTabId(store?.id ?? '')
                }} />
            </Box>

            <Box>
              <Button
                color="info"
                variant="outlined"
                onClick={handleClickViewAllRmas}>
                View All
              </Button>
            </Box>
          </>
        )}>
        <React.Fragment key={storeId ?? ''}>
          {params.tabId === 'awaitingApproval' && (
            <AwaitingApprovalTab.FC storeId={storeId} />
          )}

          {params.tabId === 'awaitingShippingLabel' && (
            <AwaitingShippingLabelTab.FC storeId={storeId} />
          )}

          {params.tabId === 'awaitingReceipt' && (
            <AwaitingItemReceiptTab.FC storeId={storeId} />
          )}

          {params.tabId === 'awaitingAcceptance' && (
            <AwaitingAcceptanceTab.FC storeId={storeId} />
          )}

          {params.tabId === 'refund' && (
            <AwaitingRefundTab.FC storeId={storeId} />
          )}
        </React.Fragment>
      </activityTemplates.AdminQueuingActivityTemplate.FC>

      {modalType2IsOpen.lookUpReturnAuthorization && (
        <LookUpReturnAuthorizationModal.FC
          fetchMode="returnAuthorization"
          onClose={() => setModalIsOpen('lookUpReturnAuthorization', false)}
          onSelect={handleSelectReturnAuthorization} />
      )}

      {modalType2IsOpen.findOrderRma && (
        <LookUpReturnAuthorizationModal.FC
          onClose={() => setModalIsOpen('findOrderRma', false)}
          onSelect={(returnAuthorizationId: string) => navigate(routeDirectory.admin.returnAuthorizations.detail.path(returnAuthorizationId))} />
      )}

      {modalType2IsOpen.createReturnAuthorization && modalType2TargetId.createReturnAuthorization === '*' && (
        <LookUpReturnAuthorizationModal.FC
          fetchMode="salesOrder"
          onClose={() => setModalIsOpen('createReturnAuthorization', false)}
          onSelect={orderId => setModalIsOpen('createReturnAuthorization', orderId)} />
      )}

      {modalType2TargetId.createReturnAuthorization && modalType2TargetId.createReturnAuthorization !== '*' && (
        <InitiateReturnModal.FC
          orderId={modalType2TargetId.createReturnAuthorization}
          onClose={() => setModalIsOpen('createReturnAuthorization', false)} />
      )}

      {modalType2IsOpen.receiveReturnAuthorization && (
        <ReceiveReturnAuthorizationModal.FC
          returnAuthorizationId={modalType2TargetId.receiveReturnAuthorization}
          onClose={() => setModalIsOpen('receiveReturnAuthorization', false)} />
      )}
    </>
  );
};
